// srt-validate-form-limits.js -  CONSTANTS - form schema for SRT calculator
// Used by front-end to validate and serialize form data
// in srt-validate.js

// Not vehicle specific, but used to validate all SRT inputs
// Specifies absolute limits for each field and step for html numeric fields etc
// as well as valid options for selects
const FORM_LIMITS = {
   VEHICLETYPE: {
      type: 'select',
      options: ['truck', 'dog-trailer', 'semi-trailer', 'pig-trailer', 'jinker-trailer', 'low-loader'],
   },
   FRONTAXLES: { min: 0, max: 4, step: 1 },
   REARAXLES: { min: 1, max: 6, step: 1 },

   F_TYRE_SIZE: { type: 'select', options: ['17.5', '19.5', '22.5'] },
   R_TYRE_SIZE: { type: 'select', options: ['17.5', '19.5', '22.5'] },
   F_TYRE_CONFIG: { type: 'select', options: ['Single', 'Wide Single', 'Dual'] },
   R_TYRE_CONFIG: { type: 'select', options: ['Single', 'Wide Single', 'Dual', 'Row of Eight'] },

   F_GROSS_MASS: { min: 0, max: 42000, step: 1 },
   R_GROSS_MASS: { min: 0, max: 108000, step: 1 },
   F_TARE_MASS: { min: 0, max: 20000, step: 1 },
   R_TARE_MASS: { min: 0, max: 20000, step: 1 },

   F_UNSPRUNG_MASS: { min: 0, max: 5000, step: 1 },
   R_UNSPRUNG_MASS: { min: 0, max: 5000, step: 1 },
   F_UNSPRUNG_CG_HEIGHT: { min: 0.01, max: 2.0 },
   R_UNSPRUNG_CG_HEIGHT: { min: 0.01, max: 2.0 },
   F_MAX_CONTACT_WIDTH: { min: 0.5, max: 5.0 },
   R_MAX_CONTACT_WIDTH: { min: 0.5, max: 5.0 },

   SPRUNG_CG_HEIGHT: { min: 0.1, max: 3.0 },
   SPRUNG_TARE_MASS: { min: 0, max: 20000, step: 1 },
   PAYLOAD_CG_HEIGHT: { min: 0.1, max: 3.0 },
   PAYLOAD_MASS: { min: 0, max: 20000, step: 1 },

   DECK_STYLE: { type: 'select', options: ['standard', 'sloping', 'step'] },
   LOAD_CATEGORY: { type: 'select', options: ['mixed-freight', 'uniform-density', 'other'] },
   LOAD_BED_HEIGHT: { min: 0.2, max: 2.0 },
   LOAD_HEIGHT_MAX: { min: 0.5, max: 20.0 }, // updated to 20m from 5
   LOAD_HEIGHT_FRONT: { min: 0.5, max: 5.0 },
   LOAD_HEIGHT_REAR: { min: 0.5, max: 5.0 },
   LOAD_BED_HEIGHT_FRONT: { min: 0.5, max: 5.0 },
   LOAD_BED_HEIGHT_REAR: { min: 0.5, max: 5.0 },

   DECK1_HEIGHT: { min: 0.5, max: 5.0 },
   DECK2_HEIGHT: { min: 0.5, max: 5.0 },
   DECK1_LENGTH: { min: 0.5, max: 20.0 },
   DECK2_LENGTH: { min: 0.5, max: 20.0 },

   // SUSPENSION_TYPE_FRONT: {
   //    type: 'select',
   //    options: ['steer', 'steelT', 'airTL', 'airTH', 'hydra', 'user'],
   // },
   // SUSPENSION_TYPE_REAR: {
   //    type: 'select',
   //    options: ['steelD', 'airDL', 'airDH', 'steelT', 'airTL', 'airTH', 'hydra', 'user'],
   // },

   SPRING_SPACING: { min: 0.5, max: 2.0 }, // meters
   LASH: { min: 0, max: 500, step: 1 }, // mm
   ROLL_STIFFNESS: { min: 0, max: 3000000, step: 1000 }, // Nm/rad
   SPRING_STIFFNESS: { min: 10000, max: 3000000, step: 1000 }, // N/m
   ROLL_CENTER_HEIGHT: { min: -1.0, max: 1.0 }, // meters (positive above axle, negative below)
};

export const formLimitsSchema = [
   {
      name: 'vehicleType',
      label: 'Vehicle Type',
      ignore: true,
      ...FORM_LIMITS.VEHICLETYPE,
   },
   {
      name: 'frontAxles',
      label: 'Front Axles',
      ...FORM_LIMITS.FRONTAXLES,
   },
   {
      name: 'rearAxles',
      label: 'Rear Axles',
      ...FORM_LIMITS.REARAXLES,
   },
   {
      name: 'f-tyre-size',
      label: 'Front Tyre Size',
      ...FORM_LIMITS.F_TYRE_SIZE,
   },
   {
      name: 'f-tyre-config',
      label: 'Front Tyre Configuration',
      ...FORM_LIMITS.F_TYRE_CONFIG,
   },
   {
      name: 'r-tyre-size',
      label: 'Rear Tyre Size',
      ...FORM_LIMITS.R_TYRE_SIZE,
   },
   {
      name: 'r-tyre-config',
      label: 'Rear Tyre Configuration',
      ...FORM_LIMITS.R_TYRE_CONFIG,
   },
   {
      name: 'f-gross-mass',
      label: 'Front Gross Mass',
      ...FORM_LIMITS.F_GROSS_MASS,
   },
   {
      name: 'r-gross-mass',
      label: 'Rear Gross Mass',
      ...FORM_LIMITS.R_GROSS_MASS,
   },
   {
      name: 'f-tare-mass',
      label: 'Front Tare Mass',
      ...FORM_LIMITS.F_TARE_MASS,
   },
   {
      name: 'r-tare-mass',
      label: 'Rear Tare Mass',
      ...FORM_LIMITS.R_TARE_MASS,
   },
   {
      name: 'f-unsprung-mass',
      label: 'Front Unsprung Mass',
      ...FORM_LIMITS.F_UNSPRUNG_MASS,
   },
   {
      name: 'r-unsprung-mass',
      label: 'Rear Unsprung Mass',
      ...FORM_LIMITS.R_UNSPRUNG_MASS,
   },
   {
      name: 'f-unsprung-cg-height',
      label: 'Front Unsprung Centre of Gravity Height',
      ...FORM_LIMITS.F_UNSPRUNG_CG_HEIGHT,
   },
   {
      name: 'r-unsprung-cg-height',
      label: 'Rear Unsprung Centre of Gravity Height',
      ...FORM_LIMITS.R_UNSPRUNG_CG_HEIGHT,
   },
   {
      name: 'f-max-contact-width',
      label: 'Front Ground Contact Width',
      ...FORM_LIMITS.F_MAX_CONTACT_WIDTH,
   },
   {
      name: 'r-max-contact-width',
      label: 'Rear Ground Contact Width',
      ...FORM_LIMITS.R_MAX_CONTACT_WIDTH,
   },
   {
      name: 'sprung-cg-height',
      label: 'Sprung Tare Mass Centre of Gravity Height',
      ...FORM_LIMITS.SPRUNG_CG_HEIGHT,
   },
   {
      name: 'sprung-tare-mass',
      label: 'Sprung Tare Mass',
      ignore: true,
      ...FORM_LIMITS.SPRUNG_TARE_MASS,
   },
   {
      name: 'payload-cg-height',
      label: 'Payload Centre of Gravity Height',
      ...FORM_LIMITS.PAYLOAD_CG_HEIGHT,
   },
   // {
   //    name: 'f-payload-mass',
   //    label: 'Payload Mass (calculated)',
   //    ...FORM_LIMITS.PAYLOAD_MASS,
   // },
   // {
   //    name: 'r-payload-mass',
   //    label: 'Payload Mass (calculated)',
   //    ...FORM_LIMITS.PAYLOAD_MASS,
   // },
   {
      name: 'deck-style',
      label: 'Deck Style',
      ...FORM_LIMITS.DECK_STYLE,
   },
   {
      name: 'load-category',
      label: 'Load Category',
      ...FORM_LIMITS.LOAD_CATEGORY,
   },
   {
      name: 'load-bed-height',
      label: 'Load Bed Height from Ground',
      ...FORM_LIMITS.LOAD_BED_HEIGHT,
   },
   {
      name: 'load-height-max',
      label: 'Maximum Load Height from Ground',
      ...FORM_LIMITS.LOAD_HEIGHT_MAX,
   },
   /*
      sloping: new Set(['load-height-front', 'load-height-rear', 'load-bed-height-front', 'load-bed-height-rear']),
   */
   {
      name: 'load-height-front',
      label: 'Load Height (Front)',
      ...FORM_LIMITS.LOAD_HEIGHT_FRONT,
   },
   {
      name: 'load-height-rear',
      label: 'Load Height (Rear)',
      ...FORM_LIMITS.LOAD_HEIGHT_REAR,
   },
   {
      name: 'load-bed-height-front',
      label: 'Load Bed Height (Front)',
      ...FORM_LIMITS.LOAD_BED_HEIGHT_FRONT,
   },
   {
      name: 'load-bed-height-rear',
      label: 'Load Bed Height (Rear)',
      ...FORM_LIMITS.LOAD_BED_HEIGHT_REAR,
   },
   /*
      step: new Set(['deck1-height', 'deck2-height', 'deck1-length', 'deck2-length']),
   */
   {
      name: 'deck1-height',
      label: 'Load Bed Height (Front - Step Deck)',
      ...FORM_LIMITS.DECK1_HEIGHT,
   },
   {
      name: 'deck2-height',
      label: 'Load Bed Height (Rear - Step Deck)',
      ...FORM_LIMITS.DECK2_HEIGHT,
   },
   {
      name: 'deck1-length',
      label: 'Load Bed Length (Front - Step Deck)',
      ...FORM_LIMITS.DECK1_LENGTH,
   },
   {
      name: 'deck2-length',
      label: 'Load Bed Length (Rear - Step Deck)',
      ...FORM_LIMITS.DECK2_LENGTH,
   },
   {
      name: 'deck-load-height-max',
      label: 'Maximum Load Height from Ground',
      ...FORM_LIMITS.LOAD_HEIGHT_MAX,
   },
   // suspension
   {
      name: 'suspensionTypeFront',
      label: 'Suspension Type (Front)',
      ...FORM_LIMITS.SUSPENSION_TYPE_FRONT,
   },
   {
      name: 'suspensionTypeRear',
      label: 'Suspension Type (Rear)',
      ...FORM_LIMITS.SUSPENSION_TYPE_REAR,
   },
   {
      name: 'f-spring-spacing',
      label: 'Front Spring Spacing',
      ...FORM_LIMITS.SPRING_SPACING,
   },
   {
      name: 'f-lash',
      label: 'Front Lash',
      ...FORM_LIMITS.LASH,
   },
   {
      name: 'f-roll-stiffness',
      label: 'Front Roll Stiffness',
      ...FORM_LIMITS.ROLL_STIFFNESS,
   },
   {
      name: 'f-spring-stiffness',
      label: 'Front Spring Stiffness',
      ...FORM_LIMITS.SPRING_STIFFNESS,
   },
   {
      name: 'f-roll-center-height',
      label: 'Front Roll Center Height',
      ...FORM_LIMITS.ROLL_CENTER_HEIGHT,
   },
   {
      name: 'r-spring-spacing',
      label: 'Rear Spring Spacing',
      ...FORM_LIMITS.SPRING_SPACING,
   },
   {
      name: 'r-lash',
      label: 'Rear Lash',
      ...FORM_LIMITS.LASH,
   },
   {
      name: 'r-roll-stiffness',
      label: 'Rear Roll Stiffness',
      ...FORM_LIMITS.ROLL_STIFFNESS,
   },
   {
      name: 'r-spring-stiffness',
      label: 'Rear Spring Stiffness',
      ...FORM_LIMITS.SPRING_STIFFNESS,
   },
   {
      name: 'r-roll-center-height',
      label: 'Rear Roll Center Height',
      ...FORM_LIMITS.ROLL_CENTER_HEIGHT,
   },
];
